<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import {
		Toolbar,
		IconButton,
		Breadcrumbs,
		Button,
		Input,
		BreadcrumbsFlexWrapper,
	} from '@Shelly/shelly-design-system/index';
	import { dashboards, dashboardTabs, dashesSearchValue } from 'src/stores/features/dashboards';
	import { edit, dashEdit } from 'src/stores/ui/edit_lists';
	import { format } from 'svelte-i18n';
	import { location, params, push } from 'svelte-spa-router';
	import DashActions from '../DashActions/DashActions.svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	import { DEFAULT_DASH_ID } from '../dash-helpers';

	const dipatch = createEventDispatcher();

	$: menuListItemsData = [
		{
			title: $format(`manage_dashboard_group_title`, { default: `Manage dashboard` }),
			isGroupTitle: true,
			items: [
				{
					title: $format(`label_edit_dashboard`, { default: 'Edit dashboard' }),
					icon: 'fas fa-pen',
					onClick: () => {
						dipatch('edit-dashboard');
					},
				},
				{
					title: $format(`label_delete_dashboard`, { default: 'Delete dashboard' }),
					icon: `fas fa-trash ${$params?.id?.toString() === DEFAULT_DASH_ID.toString() ? '' : 'text-danger'}`,
					iconMobile: 'fas fa-trash text-danger',
					titleClasses: 'text-danger',
					disabled: $params?.id?.toString() === `${DEFAULT_DASH_ID}`,
					onClick: () => {
						dipatch('delete-dashboard');
					},
				},
			],
		},
		{
			title: $format(`manage_widgets_group_title`, { default: `Manage widgets` }),
			isGroupTitle: true,
			items: [
				{
					title: $format(`label_edit_widgets`, { default: 'Edit widgets' }),
					icon: 'fas fa-pen-to-square',
					onClick: () => {
						edit.set(true);
					},
					disabled: $dashboards[$params?.id]?.dashWidgets?.length === 0,
				},
			],
		},
	];

	let windowInnerWidth: number;
	let currentDashBreadcrumbsLinks = [];

	$: isMobile = windowInnerWidth <= SystemScreenBreakpoints.MOBILE_MAX;
	$: isDashList = $location === '/dash';
	$: dashTitle = $dashboards?.[$params?.id || $location?.split('/').filter(Boolean)?.[1]]?.name || '';
	$: if (isMobile) {
		currentDashBreadcrumbsLinks = [{ label: dashTitle }];
	} else {
		currentDashBreadcrumbsLinks = [
			{
				label: $format('label_all_dashboards', { default: 'All Dashboards' }),
				onClick: onBackClick,
			},
			{ label: dashTitle },
		];
	}

	const onExitEditMode = () => {
		edit.set(false);
	};

	const onBackClick = () => {
		onExitEditMode();
		push('/dash');
	};

	const toggleEditModeDashes = () => {
		$dashEdit = !$dashEdit;
	};

	$: dashItems = $dashboardTabs.filter((dash) =>
		$dashesSearchValue.trim().length === 0
			? true
			: dash.text.toLowerCase().includes($dashesSearchValue.toLowerCase()),
	);
</script>

<svelte:window bind:innerWidth={windowInnerWidth} />

{#if isDashList}
	<Toolbar
		class="mb-m"
		hideSlotBottom={!isMobile || (isMobile && !$edit)}
		hideSlotRightSide={isMobile}
		hideSlotLeftSide={$dashEdit}
	>
		<BreadcrumbsFlexWrapper slot="breadcrumbs" gap="s" hideSlotRight={!isMobile}>
			<Breadcrumbs
				class="flex-1 min-width-0 max-width-fit-content"
				slot="left"
				listClasses="width-full"
				aria-label="Dash breadcrumbs"
				links={[{ label: $format('label_all_dashboards', { default: 'All Dashboards' }) }]}
			/>
			<Button
				slot="right"
				{...!$dashEdit ? { icon: 'fas fa-arrows-up-down-left-right' } : {}}
				size="m"
				variant="secondary"
				disabled={dashItems.length <= 1}
				on:click={toggleEditModeDashes}
			>
				{$dashEdit
					? $format('label_finish_reordering', { default: 'Finish reordering' })
					: $format('label_reorder_items', { default: 'Reorder items' })}
			</Button>
		</BreadcrumbsFlexWrapper>
		<svelte:fragment slot="leftSide">
			{#if !$dashEdit}
				<Input
					aria-label="search-dashes-input"
					id="search-dashes-id"
					icon="fas fa-magnifying-glass"
					class="width-md-l width-sm-full"
					bind:value={$dashesSearchValue}
					label={capFirstChar($format('search', { default: 'Search' }))}
				/>
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="rightSide">
			<Button
				{...!$dashEdit ? { icon: 'fas fa-arrows-up-down-left-right' } : {}}
				size="m"
				variant="secondary"
				disabled={dashItems.length <= 1}
				on:click={toggleEditModeDashes}
			>
				{$dashEdit
					? $format('label_finish_reordering', { default: 'Finish reordering' })
					: $format('label_reorder_items', { default: 'Reorder items' })}
			</Button>
		</svelte:fragment>
	</Toolbar>
{:else}
	<Toolbar
		class="mb-m"
		hideSlotLeftSide={!isMobile || (isMobile && !$edit)}
		hideSlotRightSide={isMobile || ($edit && isMobile)}
	>
		<BreadcrumbsFlexWrapper slot="breadcrumbs" hideSlotRight={!isMobile || $edit}>
			<div slot="left" class="flex-1 min-width-0 max-width-fit-content">
				<div class="d-flex width-full align-items-center">
					{#if isMobile}
						<IconButton
							aria-label="Dash back button"
							variant="secondary"
							icon="fas fa-chevron-left"
							class="mr-s-plus"
							on:click={onBackClick}
						/>
					{/if}
					<Breadcrumbs
						class="flex-1 min-width-0 max-width-fit-content"
						listClasses="width-full"
						aria-label="Dash breadcrumbs"
						type="buttons"
						links={currentDashBreadcrumbsLinks}
					/>
				</div>
			</div>
			<svelte:fragment slot="right">
				<DashActions {menuListItemsData} />
			</svelte:fragment>
		</BreadcrumbsFlexWrapper>

		<Button slot="leftSide" width="full" variant="secondary" size="m" on:click={onExitEditMode}>
			{$format('button_exit_edit', { default: 'exit edit mode' })}
		</Button>

		<svelte:fragment slot="rightSide">
			{#if $edit}
				<Button width="full" variant="secondary" size="m" on:click={onExitEditMode}>
					{$format('button_exit_edit', { default: 'exit edit mode' })}
				</Button>
			{:else}
				<DashActions {menuListItemsData} />
			{/if}
		</svelte:fragment>
	</Toolbar>
{/if}
